// absolute imports
import queryString from 'query-string';

// relative imports
import {
  adminViewingClientVar,
  currentAccountIdVar,
  customLogoUrlVar,
} from '../../../data/apollo/cache/reactiveVars';
import { primaryStoreKey } from '../../../data/apollo/cache/utilities/browserStorage';
import { currentTaxYear } from '../../../data/app-config/appDetails';
import { getAppConfig } from '../../../data/app-config/configs/configUtilities';
import { guessTimezone } from './dateUtilities';

export const getAuth0DefaultClientOptions = () => {
  const { search } = window ? window.location : {};
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { org_id, organization } = queryString.parse(search);

  return {
    domain: import.meta.env.REACT_APP_AUTH0_DOMAIN,
    clientId: import.meta.env.REACT_APP_AUTH0_DEFAULT_CLIENT_ID,
    useRefreshTokens: true,
    useRefreshTokensFallback: true,
    leeway: 60,
    authorizationParams: {
      audience: import.meta.env.REACT_APP_AUTH0_AUDIENCE,
      redirect_uri: `${import.meta.env.REACT_APP_AUTH0_CALLBACK}/auth-callback`,
      scope:
        'openid profile email read:current_user update:current_user_metadata create:current_user_metadata offline_access',
      organization:
        import.meta.env.REACT_APP_AUTH0_ORGANIZATION_ID ||
        // eslint-disable-next-line camelcase
        org_id ||
        organization,
    },
    skipRedirectCallback: [
      '/oauth-callback/',
      '/accounting/integrations/qbo',
      '/accounting/integrations/xero',
    ].some((path) => window.location.pathname.includes(path)),
  };
};

/** Returns custom logo if set for the application in Auth0, else returns custom logo by application type config or specified app logo
 * @param {String} bgType - optional - the bg type of the logo, defaults to light bg, but can pass 'dark' here
 */
export const getAppLogoSrc = (bgType) => {
  // pull custom logo if set on auth0 application
  const customLogo = customLogoUrlVar();

  if (customLogo) {
    return customLogo;
  }

  const appConfig = getAppConfig();

  // else determine logo from application type customization or default(s)
  const { logoUrl, darkLogoUrl } = appConfig;

  if (bgType === 'dark') return darkLogoUrl;
  return logoUrl;
};

export const getAppTitle = () => {
  const appConfig = getAppConfig();

  if (appConfig.appTitle) {
    return appConfig.appTitle;
  }

  return 'Ledgible Crypto';
};

/**
 * Get appropriate Auth0 Redirect URI value based on various conditions
 */
export const getAuth0RedirectUri = () => {
  const { protocol, host } = window.location;
  let redirectUri = `${protocol}//${host}/`;

  // return updated redirect or default to domain URI
  return redirectUri;
};

/** Returns an updated string after replacing instances of aliased appIDs with their respective alias
 * @param {String} path - string containing the react-router path portion of the url (may include tax, accounting, tax-preparer strings)
 * @param {Object} aliases - object containing aliases to map, defaults to tax|tax-preparer|accounting
 * @returns {String} - string with app IDs updated with their corresponding alias if applicable
 */
export const mapAliasToPath = (
  path = '',
  aliases = {
    taxAppUrlAlias: 'tax',
    taxProAppUrlAlias: 'tax-preparer',
    tirAdminAppUrlAlias: 'tir-admin',
    accountingAppUrlAlias: 'accounting',
  },
) => {
  let transform = path;
  const {
    taxAppUrlAlias,
    taxProAppUrlAlias,
    tirAdminAppUrlAlias,
    accountingAppUrlAlias,
  } = aliases;

  if (taxAppUrlAlias !== 'tax' && !path.includes('tax-preparer')) {
    transform = transform.replace('tax', taxAppUrlAlias);
  }
  if (taxProAppUrlAlias !== 'tax-preparer') {
    transform = transform.replace('tax-preparer', taxProAppUrlAlias);
  }
  if (tirAdminAppUrlAlias !== 'tir-admin') {
    transform = transform.replace('tir-admin', tirAdminAppUrlAlias);
  }
  if (accountingAppUrlAlias !== 'accounting') {
    transform = transform.replace('accounting', accountingAppUrlAlias);
  }
  return transform;
};

export const getAccountDetailsForCreation = ({ auth0Username }) => {
  const testRegex = /[^a-zA-Z0-9- ]/gi;
  const accountDetails = {
    timezone: guessTimezone(),
  };

  accountDetails.name = `${auth0Username.replace(testRegex, '')}`;
  accountDetails.taxPayerDetails = {
    taxYear: currentTaxYear,
  };

  return accountDetails;
};

// check if node env is prod
export const isDev = () => !!import.meta.env.DEV;

// clear cache and session storage for logout
export const clearCacheForLogout = async (apolloClient) => {
  // reset local storage
  sessionStorage.removeItem(primaryStoreKey);
  // remove all data with table/filter state
  sessionStorage.removeItem('appState');

  // stop and clear apollo store
  if (apolloClient) {
    apolloClient.stop();
    await apolloClient.clearStore();
  }
};

// add contextual elements to navigation path
/** @param {string} [pathWithoutApp='/'] — Defaults to `/` - Pass `''` to append nothing after the client prefix. */
export const getContextualAppPath = (pathWithoutApp = '/') => {
  const adminViewingClient = adminViewingClientVar();
  const currentAccountId = currentAccountIdVar();
  let path = '';

  if (adminViewingClient) {
    path += `/client/${currentAccountId}`;
  }

  if (pathWithoutApp) {
    path += pathWithoutApp;
  }

  return path;
};
